import React, { useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  SortingState,
  getSortedRowModel,
  ColumnFiltersState,
  getFilteredRowModel,
  VisibilityState,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { Edit2, Trash2, Download, Search, AlertTriangle, X, RotateCcw } from "lucide-react";
import { usePermissions } from "../hooks/usePermissions";

const ACCENT      = "#2563eb";
const HEADER_BG   = "#1e3a8a";
const BORDER      = "#dbe4f0";
const SURFACE     = "#f4f7fb";
const TEXT_MAIN   = "#1e293b";
const TEXT_MUTED  = "#64748b";
const MONO        = "'DM Mono', monospace";
const SORA        = "'Plus Jakarta Sans', sans-serif";

const DEFAULT_QA_PREFIX = "data-table2";
const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
const buildQaId = (prefix: string, suffix: string | number) => `${qaSafe(prefix)}-${qaSafe(suffix)}`;
const qaAttrs = (prefix: string, suffix: string | number) => {
  const id = buildQaId(prefix, suffix);
  return {
    id,
    "data-testid": id,
    "data-qa": id,
  };
};

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  onEdit?: (row: TData) => void;
  onDelete?: (id: string | number) => void;
  onRestore?: (id: string | number) => void;
  onDownload?: (row: TData) => void;
  searchColumns?: string[];
  /** Controlled search value for server-side filtering. */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  /** Optional helper text shown as a small tooltip when the search field is hovered or focused. */
  searchHelpText?: string;
  addButtonLabel?: string;
  validateButtonLabel?: string;
  onValidate?: () => void;
  showAddButton?: boolean;
  onAdd?: () => void;
  onEditClick?: (row: TData) => void;
  toolbarExtras?: React.ReactNode;
  /** Optional upload action placed at the far right of the compact toolbar. */
  toolbarUpload?: React.ReactNode;
  /** Applies the compact single-row toolbar layout used by rule configuration pages. */
  compactToolbar?: boolean;
  pageIndex?: number;
  onPageChange?: (page: number) => void;
  /** Enables server-side pagination. Existing pages remain client-side by default. */
  manualPagination?: boolean;
  /** Total number of Backend pages when manualPagination is enabled. */
  pageCount?: number;
  /** Optional total number of matching Backend records. */
  totalCount?: number;
  /** Controlled page size. Leave undefined to keep the original internal behavior. */
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  /** Page sizes shown in the selector. */
  allowedPageSizes?: number[];
  /** Shows a loading row without removing the table structure. */
  isLoading?: boolean;
  rowClassName?: (row: TData) => string;
  isRowDeleted?: (row: TData) => boolean;
  isRowInactive?: (row: TData) => boolean;
  /** When false, hides the hard-delete (permanent delete) icon on already soft-deleted rows.
   *  Soft delete on active rows is still allowed. Default: true. */
  canHardDelete?: boolean;
  /** Optional QA prefix to keep automation IDs unique when the same table component is used on multiple pages. */
  qaIdPrefix?: string;
}

export function DataTable2<TData, TValue>({
  columns,
  data,
  onEdit,
  onDelete,
  onRestore,
  onDownload,
  searchColumns,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  searchHelpText,
  addButtonLabel = "Add New",
  validateButtonLabel,
  showAddButton = true,
  onAdd,
  onValidate,
  onEditClick,
  toolbarExtras,
  toolbarUpload,
  compactToolbar = false,
  pageIndex = 0,
  onPageChange,
  manualPagination = false,
  pageCount,
  totalCount,
  pageSize,
  onPageSizeChange,
  allowedPageSizes = [5, 10, 20, 30, 40, 50],
  isLoading = false,
  rowClassName,
  isRowDeleted,
  isRowInactive,
  canHardDelete = true,
  qaIdPrefix = DEFAULT_QA_PREFIX,
}: DataTableProps<TData, TValue>) {
  const { isViewer } = usePermissions();
  const [sorting,          setSorting]          = useState<SortingState>([]);
  const [columnFilters,    setColumnFilters]    = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(pageSize ?? 5);
  const [globalFilter,     setGlobalFilter]     = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete,     setItemToDelete]     = useState<string | number | null>(null);
  const [isDeletedRow,     setIsDeletedRow]     = useState(false);
  const [blockedToast,     setBlockedToast]     = useState(false);
  const [searchHovered,    setSearchHovered]    = useState(false);
  const [searchFocused,    setSearchFocused]    = useState(false);

  const getQaAttrs = (suffix: string | number) => qaAttrs(qaIdPrefix, suffix);
  const getRowIdForQa = (row: any) => row?.id ?? row?.Id ?? row?.ID ?? "unknown";
  const effectivePageSize = pageSize ?? internalRowsPerPage;
  const effectiveGlobalFilter = searchValue ?? globalFilter;
  const hasSearch = Boolean(
    (searchColumns && searchColumns.length > 0) ||
    searchValue !== undefined ||
    onSearchChange
  );
  const updateGlobalFilter = (nextValue: string) => {
    if (searchValue === undefined) setGlobalFilter(nextValue);
    onSearchChange?.(nextValue);
  };
  const showSearchHelp = Boolean(searchHelpText && (searchHovered || searchFocused));
  const effectivePageCount = manualPagination
    ? Math.max(1, Number(pageCount) || 1)
    : undefined;
  const hasActions = Boolean(onEdit || onDelete || onDownload || onRestore);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel:       getCoreRowModel(),
    onSortingChange:       setSorting,
    getSortedRowModel:     getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel:   getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination,
    pageCount: effectivePageCount,
    onColumnVisibilityChange: setColumnVisibility,
    getRowId: (row) => String((row as any).id ?? Math.random()),
    state: { sorting, columnFilters, columnVisibility, globalFilter: effectiveGlobalFilter, pagination: { pageIndex, pageSize: effectivePageSize } },
    globalFilterFn: (row, _columnIds, filterValue) => {
      if (!searchColumns || searchColumns.length === 0) return true;
      return searchColumns.some((colId) => {
        const cellValue = row.getValue(colId);
        return cellValue?.toString().toLowerCase().includes(filterValue.toLowerCase());
      });
    },
    onGlobalFilterChange: (updater) => {
      const nextValue = typeof updater === "function"
        ? updater(effectiveGlobalFilter)
        : updater;
      updateGlobalFilter(nextValue);
    },
    autoResetPageIndex: false,
  });

  const handleDeleteClick = (id: string | number, deleted: boolean) => {
    if (deleted) {
      if (onDelete) onDelete(id);
    } else {
      setIsDeletedRow(false);
      setItemToDelete(id);
      setDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = () => {
    if (itemToDelete !== null && onDelete) {
      onDelete(itemToDelete);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const showBlockedToast = () => {
    setBlockedToast(true);
    setTimeout(() => setBlockedToast(false), 3000);
  };

  const displayedPageCount = manualPagination
    ? Math.max(1, Number(pageCount) || 1)
    : Math.max(1, table.getPageCount());
  const canGoPrevious = manualPagination
    ? pageIndex > 0
    : table.getCanPreviousPage();
  const canGoNext = manualPagination
    ? pageIndex + 1 < displayedPageCount
    : table.getCanNextPage();

  const handlePreviousPage = () => {
    if (!canGoPrevious) return;
    if (!manualPagination) table.previousPage();
    onPageChange?.(Math.max(0, pageIndex - 1));
  };

  const handleNextPage = () => {
    if (!canGoNext) return;
    if (!manualPagination) table.nextPage();
    onPageChange?.(pageIndex + 1);
  };

  const handlePageSizeChange = (value: number) => {
    if (pageSize === undefined) setInternalRowsPerPage(value);
    if (!manualPagination) table.setPageSize(value);
    onPageSizeChange?.(value);
    onPageChange?.(0);
  };

  return (
    <>
      <style>{`
        .dt2-row:hover td { background: #eff6ff !important; }
        .dt2-row-deleted td { background: #f8f8f8 !important; opacity: 0.6; }
        .dt2-row-deleted:hover td { background: #f1f1f1 !important; }
        .dt2-row-inactive td { background: #fef9f9 !important; opacity: 0.75; }
        .dt2-row-inactive:hover td { background: #fee2e2 !important; }
        .dt2-header-content,
        .dt2-header-content * {
          font-family: ${SORA} !important;
          font-size: 13px !important;
          font-weight: 600 !important;
          color: #fff !important;
          text-transform: none !important;
        }
        @keyframes dt2-spin { to { transform: rotate(360deg); } }
        @keyframes dt2-fadein { from { opacity:0; transform: translateY(-8px); } to { opacity:1; transform: translateY(0); } }
      `}</style>

      {blockedToast && (
        <div
          {...getQaAttrs("toast-blocked-deleted-row")}
          role="alert"
          style={{
          position: "fixed", top: 24, right: 24, zIndex: 9999,
          background: "#1e3a8a", color: "#fff", borderRadius: 12,
          padding: "14px 20px", fontFamily: SORA, fontSize: 13, fontWeight: 600,
          boxShadow: "0 8px 24px rgba(0,0,0,0.18)", display: "flex", alignItems: "center", gap: 10,
          animation: "dt2-fadein 0.2s ease",
        }}>
          <AlertTriangle
            {...getQaAttrs("icon-toast-blocked-deleted-row")}
            size={16}
            color="#fbbf24"
          />
          <span {...getQaAttrs("toast-blocked-deleted-row-message")}>
            This record is deleted. Please restore it first.
          </span>
        </div>
      )}

      <div {...getQaAttrs("root")} style={{ width: "100%", fontFamily: SORA }}>

        <div
          {...getQaAttrs("toolbar")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: compactToolbar ? 10 : 8,
            flexWrap: "wrap",
            marginBottom: 16,
            ...(compactToolbar
              ? {
                  width: "100%",
                  padding: "10px 16px",
                  background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                  borderRadius: 12,
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  boxSizing: "border-box" as const,
                }
              : {}),
          }}
        >
          {showAddButton && (
            <button
              {...getQaAttrs("btn-add")}
              type="button"
              onClick={onAdd}
              style={{
                background: `linear-gradient(135deg, ${HEADER_BG}, ${ACCENT})`,
                color: "#fff", border: "none", borderRadius: 9999,
                padding: "9px 18px", fontFamily: SORA, fontWeight: 600,
                fontSize: 13, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
              }}
            >
              {addButtonLabel}
            </button>
          )}

          {onValidate && (
            <button {...getQaAttrs("btn-validate")} type="button" onClick={onValidate} style={{ background: HEADER_BG, color: "#fff", border: "none", borderRadius: 9999, padding: "9px 18px", fontFamily: SORA, fontWeight: 700, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
              {validateButtonLabel}
            </button>
          )}

          {hasSearch && (
            <div
              {...getQaAttrs("global-search-wrapper")}
              onMouseEnter={() => setSearchHovered(true)}
              onMouseLeave={() => setSearchHovered(false)}
              style={{ position: "relative", flexShrink: 0, width: 220 }}
            >
              <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: ACCENT, pointerEvents: "none" }} />
              <input
                {...getQaAttrs("input-global-search")}
                aria-describedby={searchHelpText ? buildQaId(qaIdPrefix, "search-help-tooltip") : undefined}
                type="text"
                placeholder={searchPlaceholder}
                value={effectiveGlobalFilter}
                onChange={(e) => updateGlobalFilter(e.target.value)}
                style={{ width: "100%", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "7px 12px 7px 32px", color: TEXT_MAIN, fontFamily: MONO, fontSize: 13, outline: "none", boxSizing: "border-box", transition: "border-color 0.2s, box-shadow 0.2s" }}
                onFocus={(e) => {
                  setSearchFocused(true);
                  e.currentTarget.style.borderColor = ACCENT;
                  e.currentTarget.style.boxShadow = "0 0 0 3px rgba(37,99,235,0.10)";
                }}
                onBlur={(e) => {
                  setSearchFocused(false);
                  e.currentTarget.style.borderColor = BORDER;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
              {showSearchHelp && (
                <div
                  {...getQaAttrs("search-help-tooltip")}
                  role="tooltip"
                  style={{
                    position: "absolute",
                    top: "calc(100% + 9px)",
                    left: 0,
                    zIndex: 150,
                    width: "max-content",
                    maxWidth: 330,
                    padding: "9px 12px",
                    borderRadius: 9,
                    background: "#0f172a",
                    color: "#f8fafc",
                    boxShadow: "0 10px 28px rgba(15,23,42,0.22)",
                    fontFamily: SORA,
                    fontSize: 11,
                    fontWeight: 500,
                    lineHeight: 1.5,
                    pointerEvents: "none",
                    animation: "dt2-fadein 0.15s ease",
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      top: -5,
                      left: 18,
                      width: 10,
                      height: 10,
                      background: "#0f172a",
                      transform: "rotate(45deg)",
                    }}
                  />
                  <span style={{ position: "relative", zIndex: 1 }}>{searchHelpText}</span>
                </div>
              )}
            </div>
          )}

          {toolbarExtras && (
            <div
              {...getQaAttrs("toolbar-extras")}
              style={compactToolbar ? { display: "contents" } : undefined}
            >
              {toolbarExtras}
            </div>
          )}

          {toolbarUpload && (
            <div
              {...getQaAttrs("toolbar-upload")}
              style={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                marginLeft: compactToolbar ? 8 : undefined,
              }}
            >
              {toolbarUpload}
            </div>
          )}
        </div>
         
        <div {...getQaAttrs("table-wrapper")} style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 16, overflowX: "auto", WebkitOverflowScrolling: "touch", boxShadow: "0 2px 16px #1e3a8a10" }}>
          <table {...getQaAttrs("table")} style={{ width: "100%", minWidth: 700, borderCollapse: "collapse" }}>
            <thead {...getQaAttrs("table-head")}>
              {table.getHeaderGroups().map((hg) => (
                <tr {...getQaAttrs(`header-row-${hg.id}`)} key={hg.id} style={{ background: HEADER_BG }}>
                  {hg.headers.map((header) => (
                    <th {...getQaAttrs(`header-cell-${header.id}`)} key={header.id} style={{ padding: "14px 16px", textAlign: "left", color: "#fff", fontSize: 13, fontWeight: 600, fontFamily: SORA, whiteSpace: "nowrap", borderRight: `1px solid #ffffff18` }}>
                      {header.isPlaceholder ? null : (
                        <div className="dt2-header-content">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                      )}
                    </th>
                  ))}
                {hasActions && (
  <th
    {...getQaAttrs("header-cell-actions")}
    style={{
      padding: "14px 16px",
      textAlign: "center",
      color: "#fff",
      fontSize: 13,
      fontFamily: SORA,
    }}
  >
    <strong style={{ fontWeight: 800 }}>Actions</strong>
  </th>
)}

                  
                </tr>
              ))}
            </thead>

            <tbody {...getQaAttrs("table-body")}>
              {isLoading ? (
                <tr {...getQaAttrs("row-loading")}>
                  <td
                    {...getQaAttrs("cell-loading")}
                    colSpan={columns.length + (hasActions ? 1 : 0)}
                    style={{ padding: 52, textAlign: "center", color: TEXT_MUTED, fontFamily: MONO, fontSize: 13 }}
                  >
                    Loading...
                  </td>
                </tr>
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row, idx) => {
                  const deleted  = isRowDeleted  ? isRowDeleted(row.original)  : false;
                  const inactive = isRowInactive ? isRowInactive(row.original) : false;
                  const extraClass = rowClassName ? rowClassName(row.original) : "";

                  // ── show delete icon: soft delete on active rows always allowed;
                  //    hard delete on deleted rows only when canHardDelete is true ──
                  const showDeleteBtn = !!onDelete && (!deleted || canHardDelete);

                  return (
                    <tr
                      {...getQaAttrs(`row-${getRowIdForQa(row.original)}`)}
                      data-row-id={String(getRowIdForQa(row.original))}
                      data-row-deleted={deleted ? "true" : "false"}
                      data-row-inactive={inactive ? "true" : "false"}
                      key={(row.original as any).id ?? row.id}
                      className={`${deleted ? "dt2-row-deleted" : inactive ? "dt2-row-inactive" : "dt2-row"} ${extraClass}`}
                      style={{
                        borderBottom: idx < table.getRowModel().rows.length - 1 ? `1px solid ${BORDER}` : "none",
                        background: deleted ? "#f5f5f5" : inactive ? "#fef9f9" : idx % 2 === 0 ? "#fff" : SURFACE,
                        transition: "background 0.15s",
                        position: "relative",
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td {...getQaAttrs(`cell-${getRowIdForQa(row.original)}-${cell.column.id}`)} key={cell.id} style={{ padding: "14px 16px", fontSize: 14, color: deleted ? "#aaa" : inactive ? "#94a3b8" : TEXT_MAIN, fontFamily: MONO, fontWeight: 700, borderRight: `1px solid ${BORDER}` }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}

                      {hasActions && (
                        <td {...getQaAttrs(`cell-${getRowIdForQa(row.original)}-actions`)} style={{ padding: "14px 16px", textAlign: "center" }}>
                          <div {...getQaAttrs(`actions-${getRowIdForQa(row.original)}`)} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>

                            {onEdit && (
                              <button
                                {...getQaAttrs(`btn-edit-row-${getRowIdForQa(row.original)}`)}
                                type="button"
                                onClick={() => {
                                  if (deleted) { showBlockedToast(); return; }
                                  onEdit(row.original);
                                  onEditClick?.(row.original);
                                }}
                                title={deleted ? "Restore first to edit" : "Edit"}
                                style={{
                                  background: deleted ? "#f1f5f9" : "#eff6ff",
                                  border: `1px solid ${deleted ? "#e2e8f0" : "#bfdbfe"}`,
                                  color: deleted ? "#cbd5e1" : ACCENT,
                                  borderRadius: 7, padding: "6px 10px",
                                  cursor: deleted ? "not-allowed" : "pointer",
                                  display: "flex", alignItems: "center",
                                }}
                              >
                                <Edit2 size={14} />
                              </button>
                            )}

                            {showDeleteBtn && (
                              <button
                                {...getQaAttrs(`${deleted ? "btn-hard-delete-row" : "btn-delete-row"}-${getRowIdForQa(row.original)}`)}
                                data-qa-action={deleted ? "hard-delete" : "delete"}
                                data-row-id={String(getRowIdForQa(row.original))}
                                data-row-deleted={deleted ? "true" : "false"}
                                type="button"
                                onClick={() => handleDeleteClick((row.original as any).id, deleted)}
                                title={deleted ? "Permanently Delete" : "Delete"}
                                aria-label={`${deleted ? "Permanently delete" : "Delete"} row ${getRowIdForQa(row.original)}`}
                                style={{
                                  background: "#fef2f2",
                                  border: `1px solid #fca5a5`,
                                  color: "#dc2626",
                                  borderRadius: 7, padding: "6px 10px",
                                  cursor: "pointer",
                                  display: "flex", alignItems: "center",
                                  opacity: deleted ? 0.8 : 1,
                                }}
                              >
                                <Trash2
                                  {...getQaAttrs(`${deleted ? "icon-hard-delete-row" : "icon-delete-row"}-${getRowIdForQa(row.original)}`)}
                                  size={14}
                                />
                              </button>
                            )}

                            {onRestore && deleted && (
                              <button
                                {...getQaAttrs(`btn-restore-row-${getRowIdForQa(row.original)}`)}
                                type="button"
                                onClick={() => onRestore((row.original as any).id)}
                                title="Restore"
                                style={{
                                  background: "#f0fdf4", border: "1px solid #86efac",
                                  color: "#16a34a", borderRadius: 7, padding: "6px 10px",
                                  cursor: "pointer", display: "flex", alignItems: "center",
                                }}
                              >
                                <RotateCcw
                                  {...getQaAttrs(`icon-restore-row-${getRowIdForQa(row.original)}`)}
                                  size={14}
                                />
                              </button>
                            )}

                            {onDownload && !isViewer && (
                              <button
                                {...getQaAttrs(`btn-download-row-${getRowIdForQa(row.original)}`)}
                                type="button"
                                onClick={() => onDownload(row.original)}
                                style={{ background: "#f0fdf4", border: "1px solid #86efac", color: "#16a34a", borderRadius: 7, padding: "6px 10px", cursor: "pointer", display: "flex", alignItems: "center" }}
                              >
                                <Download size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr {...getQaAttrs("row-no-results")}>
                  <td {...getQaAttrs("cell-no-results")} colSpan={columns.length + (hasActions ? 1 : 0)} style={{ padding: 52, textAlign: "center", color: TEXT_MUTED, fontFamily: MONO, fontSize: 13 }}>
                    No results.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div {...getQaAttrs("pagination")} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ color: TEXT_MUTED, fontFamily: MONO, fontSize: 12 }}>Rows per page:</span>
            <select
              {...getQaAttrs("select-rows-per-page")}
              aria-label="Rows per page"
              value={effectivePageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "7px 12px", color: TEXT_MAIN, fontFamily: MONO, fontSize: 12, outline: "none", cursor: "pointer", appearance: "none", WebkitAppearance: "none" }}
            >
              {allowedPageSizes.map((n) => <option key={n} value={n}>{n} rows</option>)}
            </select>
            <span {...getQaAttrs("pagination-page-info")} style={{ color: TEXT_MUTED, fontFamily: MONO, fontSize: 12 }}>
              Page {pageIndex + 1} of {displayedPageCount}
              {typeof totalCount === "number" ? ` · Total ${totalCount}` : ""}
            </span>
          </div>

          <div style={{ display: "flex", gap: 6 }}>
            <button
              {...getQaAttrs("btn-pagination-prev")}
              type="button"
              onClick={handlePreviousPage}
              disabled={!canGoPrevious || isLoading}
              style={{ background: canGoPrevious && !isLoading ? "#fff" : SURFACE, border: `1px solid ${BORDER}`, color: canGoPrevious && !isLoading ? TEXT_MAIN : TEXT_MUTED, borderRadius: 8, padding: "8px 18px", fontFamily: MONO, fontSize: 12, fontWeight: 700, cursor: canGoPrevious && !isLoading ? "pointer" : "not-allowed", opacity: canGoPrevious && !isLoading ? 1 : 0.5 }}
            >
              ← Prev
            </button>
            <button
              {...getQaAttrs("btn-pagination-next")}
              type="button"
              onClick={handleNextPage}
              disabled={!canGoNext || isLoading}
              style={{ background: canGoNext && !isLoading ? ACCENT : SURFACE, border: `1px solid ${canGoNext && !isLoading ? ACCENT : BORDER}`, color: canGoNext && !isLoading ? "#fff" : TEXT_MUTED, borderRadius: 8, padding: "8px 18px", fontFamily: MONO, fontSize: 12, fontWeight: 700, cursor: canGoNext && !isLoading ? "pointer" : "not-allowed", opacity: canGoNext && !isLoading ? 1 : 0.5 }}
            >
              Next →
            </button>
          </div>
        </div>

        {deleteDialogOpen && (
          <div {...getQaAttrs("dialog-delete-overlay")} role="presentation" style={{ position: "fixed", inset: 0, background: "#00000077", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(3px)" }}>
            <div {...getQaAttrs("dialog-delete")} role="dialog" aria-modal="true" aria-labelledby={buildQaId(qaIdPrefix, "dialog-delete-title")} style={{ background: "#fff", border: `1px solid #dc262633`, borderRadius: 16, padding: 32, width: 380, textAlign: "center", boxShadow: "0 8px 40px #dc262618", position: "relative" }}>
              <button
                {...getQaAttrs("btn-delete-dialog-x-close")}
                data-qa-action="close-delete-dialog"
                type="button"
                onClick={handleCancelDelete}
                style={{ position: "absolute", top: 14, right: 14, background: SURFACE, border: "none", color: TEXT_MUTED, borderRadius: 8, width: 30, height: 30, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <X
                  {...getQaAttrs("icon-delete-dialog-x-close")}
                  size={15}
                />
              </button>
              <div
                {...getQaAttrs("delete-dialog-alert-icon-wrapper")}
                style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}
              >
                <AlertTriangle
                  {...getQaAttrs("icon-delete-dialog-alert")}
                  size={28}
                  color="#dc2626"
                />
              </div>
               <div {...getQaAttrs("dialog-delete-title")} style={{ color: HEADER_BG, fontFamily: SORA, fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Confirm Delete</div>
              <div
                {...getQaAttrs("dialog-delete-message")}
                style={{ color: TEXT_MUTED, fontFamily: MONO, fontSize: 12, marginBottom: 24, lineHeight: 1.6 }}
              >
                <span {...getQaAttrs("dialog-delete-confirmation-message")}>
                  Are you sure you want to delete this item?
                </span>
                <br />
                <span
                  {...getQaAttrs("dialog-delete-soft-delete-message")}
                  style={{ color: "#d97706", fontWeight: 700 }}
                >
                  This item will be soft-deleted. You can restore it later.
                </span>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button {...getQaAttrs("btn-delete-dialog-cancel")} data-qa-action="cancel-delete" type="button" onClick={handleCancelDelete} style={{ flex: 1, background: SURFACE, color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: 9, padding: 12, cursor: "pointer", fontFamily: SORA, fontSize: 13 }}><span {...getQaAttrs("btn-delete-dialog-cancel-label")}>Cancel</span></button>
                <button {...getQaAttrs("btn-delete-dialog-confirm")} data-qa-action="confirm-delete" type="button" onClick={handleConfirmDelete} style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontFamily: SORA, fontWeight: 700, fontSize: 13 }}><span {...getQaAttrs("btn-delete-dialog-confirm-label")}>Delete</span></button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default DataTable2;